import { useContext, useEffect, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { queryKeys } from "../../utilities/queryKeys";
import { getfounderDashboardDetails, getIntakeStatus, reapplyFromCompletedProject } from "../../services/founderService";
import * as T from "../../utilities/frontendTypes";
import { ApplicationStatus } from "@eip/shared/types/application.types";
import { LangContext, LangContextType, LoadingContext, LoadingContextType, ToastContext, ToastContextType } from "../../utilities/customHooks";
import { resolveConfig, APPROVED_IO_NF_CONFIG } from "./FounderDashboards/dashboardRouteConfig";
import GenericFounderDashboard from "./FounderDashboards/GenericFounderDashboard";
import SowDashboard from "./FounderDashboards/PreSowDashboard";
import SelectSpfFounderDashboard from "./FounderDashboards/SelectSpfFounderDashboard";
import Modal from "../../Components/Modal";
import textData from "../../textData.json";

function FounderDashboard() {
  const [isViewingPastProjects, setIsViewingPastProjects] = useState(false);
  const [viewingSow, setViewingSow] = useState<T.Statement>();
  const [appArrayPosition, setAppArrayPosition] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReapplying, setIsReapplying] = useState(false);

  const { langState } = useContext(LangContext) as LangContextType;
  const { showLoading, hideLoading } = useContext(LoadingContext) as LoadingContextType;
  const showToast = (useContext(ToastContext) as ToastContextType).showToast;

  const { data: founderDashboardData } = useSuspenseQuery({
    queryKey: queryKeys.dashboards.founder(),
    queryFn: () => getfounderDashboardDetails(),
  });
  const { data: intakeStatusData } = useSuspenseQuery({
    queryKey: queryKeys.intakeStatus(),
    queryFn: () => getIntakeStatus(),
  });

  const applications = founderDashboardData.dashboardDetails.applications;

  useEffect(() => {
    if (applications && applications.length > 0) {
      setAppArrayPosition(0);
    }
  }, [applications]);

  // Auto-select SOW if only one exists
  useEffect(() => {
    if (!founderDashboardData) return;
    const projects = founderDashboardData.dashboardDetails.projects;
    if (!projects || projects.length === 0) return;

    const active = projects.filter(project =>
      project.projectStatus === "requested" ||
      project.projectStatus === "submitted" ||
      project.projectStatus === "preSignature" ||
      project.projectStatus === "signed"
    );

    if (active.length === 1 && active[0].statements.length === 1 && !viewingSow?.sowId) {
      setViewingSow(active[0].statements[0]);
    }
  }, [founderDashboardData, viewingSow?.sowId]);

  const dashboardDetails = founderDashboardData.dashboardDetails;
  const userRegion = dashboardDetails.userRegion;
  const isMvScope = dashboardDetails.isMvScope;
  const appArray = dashboardDetails.applications;

  const companyName = appArray?.[appArrayPosition]
    ?.profileSnapshot.companyInfo?.basicInfo?.operatingName
    || dashboardDetails.companyName;

  const intakeStatus = intakeStatusData ? intakeStatusData.data as T.IntakeStatus : null;
  const isIntakeOpen = intakeStatus?.isOpen ?? true;

  const completedProjects: T.Project[] = [];
  const inProgressProjects: T.Project[] = [];

  if (dashboardDetails.projects && dashboardDetails.projects.length > 0) {
    dashboardDetails.projects.forEach((project: T.Project) => {
      if (project.projectStatus === "completed" || project.projectStatus === "payment requested" || project.projectStatus === "paid") {
        completedProjects.push(project);
      }
      if (project.projectStatus === "requested" || project.projectStatus === "submitted" || project.projectStatus === "preSignature" || project.projectStatus === "signed") {
        inProgressProjects.push(project);
      }
    });
  }

  const handleViewingSow = (sowId: string) => {
    if (dashboardDetails.projects && dashboardDetails.projects.length > 0) {
      for (const project of dashboardDetails.projects) {
        if (project.statements && Array.isArray(project.statements)) {
          for (const statement of project.statements) {
            if (statement.sowId === sowId) {
              setViewingSow(statement);
            }
          }
        }
      }
    }
  };

  const activeStatements = inProgressProjects?.[0]?.statements ?? [];

  const handleReapply = async () => {
    if (isReapplying) return;
    setIsReapplying(true);
    showLoading();
    try {
      const response = await reapplyFromCompletedProject();
      hideLoading();
      if (response.success) {
        showToast({ toastType: "success", message: "New application created. You can now update and submit.", autoDisappearTimeInSeconds: 5 });
        window.location.href = "/dashboard/profile";
      } else {
        showToast({ toastType: "failure", message: "Failed to create new application.", autoDisappearTimeInSeconds: 8 });
        setIsReapplying(false);
      }
    } catch {
      hideLoading();
      showToast({ toastType: "failure", message: "Failed to create new application.", autoDisappearTimeInSeconds: 8 });
      setIsReapplying(false);
    }
  };

  // --- Resolve dashboard config ---
  const status: ApplicationStatus = appArray?.[appArrayPosition]?.status ?? "unsubmitted";
  const dashboardConfig = resolveConfig(status, userRegion, isMvScope);

  // --- Render standalone dashboards ---
  if (dashboardConfig.kind === "standalone") {
    switch (dashboardConfig.component) {
      case "PreSowDashboard":
        return (
          <SowDashboard
            companyNameForDashboard={companyName}
            percentage={inProgressProjects[0].coPayPercentage.toString()}
            firm={inProgressProjects[0].spName}
            sow={viewingSow ? viewingSow : dashboardDetails.projects[0].statements[0]}
            statements={activeStatements}
            onTierChange={handleViewingSow}
          />
        );
      case "SelectSpfFounderDashboard":
        return <SelectSpfFounderDashboard companyNameForDashboard={companyName} />;
    }
  }

  // --- Render generic dashboards ---
  const config = dashboardConfig;

  // ExpertInvite modal content
  const expertInviteModal = status === "ipExpertInvite" ? (
    <Modal
      isOpen={isModalOpen}
      closeModal={() => setIsModalOpen(false)}
      modalHeader=""
      contentLabel="IP Advisor Assessment"
      modalHTML={
        <div className="assessmentModalCont">
          <div className="text-center mb-8">
            <h1>{(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleTitle[langState]}</h1>
            <h3>{(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleTitleSub[langState]}</h3>
          </div>
          <div className="flex flex-col gap-8">
            <p>{(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleP1[langState]}</p>
            <p>{(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleP2[langState]}</p>
            <ul className="list-disc ml-8">
              <li className="mb-8">
                {(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleLi1[langState]}
                <li className="ml-6 mt-1">
                  {(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleLi2[langState]}
                </li>
              </li>
              <li className="mb-8">
                {(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleLi3[langState]}
              </li>
              <li className="mb-8">
                {(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleLi4[langState]}
                <li className="ml-6 mt-1">
                  {(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleLi5[langState]}
                </li>
              </li>
              <li className="mb-8">
                {(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleLi6[langState]}
                <li className="ml-6 mt-1">
                  {(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleLi7[langState]}
                </li>
              </li>
              <li>
                {(textData.Pages.Dashboards.FounderDashboards.ExpertInviteDashboard as Record<string, Record<string, string>>).ModaleLi8[langState]}
              </li>
            </ul>
          </div>
        </div>
      }
    />
  ) : undefined;

  // ProjectCompleted custom children (info box + reason note label)
  const isProjectCompleted = ["projectCompleted", "partnerOrgProjectCompleted", "closed"].includes(status);
  const isSurveyPhase = ["sowComplete", "surveySent", "surveyComplete"].includes(status);
  const projectCompletedChildren = (isProjectCompleted || isSurveyPhase) ? (
    <div className="mt-4 mb-2 p-3 bg-[#f0f9f4] rounded-md border border-[#4F9D7A]/20">
      <p className="text-sm text-gray-700">
        <span className="font-semibold">{langState === "fr" ? "Remarque :" : "Note:"}</span>{" "}
        {(textData as Record<string, unknown> & { Pages: { Dashboards: { FounderDashboards: Record<string, Record<string, Record<string, string>>> } } }).Pages.Dashboards.FounderDashboards.ProjectCompletedFounderDashboard?.PreFillNote?.[langState] ??
          "Your company information and personal details will be pre-filled from your previous application. You'll need to fill out the \"Need Help With\" sections again to reflect your current needs."}
      </p>
    </div>
  ) : undefined;

  // The IO/NF approved dashboard was a standalone early-return before the
  // 2026-07-12 fold and never rendered the past-projects wrapper — preserve
  // that (identity check works: the config is a frozen singleton).
  const hidePastProjects = config === APPROVED_IO_NF_CONFIG;

  return (
    <div>
      {completedProjects.length > 0 && !hidePastProjects && (
        <div>
          <h3>{isViewingPastProjects ? "Current Projects" : "Past Projects"}</h3>
        </div>
      )}
      {isViewingPastProjects && (
        <div>
          {completedProjects.map((project, index) => (
            <div key={project._id}>
              <button>Project {index + 1}</button>
            </div>
          ))}
        </div>
      )}
      <GenericFounderDashboard
        config={config}
        companyNameForDashboard={companyName}
        isIntakeOpen={isIntakeOpen}
        intakeStatus={intakeStatus}
        midApplication={dashboardDetails.midApplication}
        onButtonClick={
          status === "ipExpertInvite" ? () => setIsModalOpen(true)
          : isProjectCompleted ? handleReapply
          : undefined
        }
        modalContent={expertInviteModal}
      >
        {projectCompletedChildren}
      </GenericFounderDashboard>
    </div>
  );
}

export default FounderDashboard;
